import { AppShell, Burger } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Navigate, Outlet, useMatches } from 'react-router'
import { useEffect } from 'react'
import { notifications } from '@mantine/notifications'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { useAuthStore } from '../../stores/authStore'
import type { RouteMeta } from '@paperair/shared-types'

export function AppLayout() {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'Admin'

  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure()
  const [desktopCollapsed, { toggle: toggleDesktop }] = useDisclosure()

  const matches = useMatches()

  useEffect(() => {
    if (isAdmin) return
    notifications.show({
      title: 'Access restricted',
      message: "You don't have access to the Accounts admin portal.",
      color: 'orange',
    })
  }, [isAdmin])

  useEffect(() => {
    const match = [...matches]
      .reverse()
      .find(
        (m): m is typeof m & { handle: RouteMeta } =>
          typeof m.handle === 'object' && m.handle!== null
      )

    if (!match) return

    const { handle, data } = match

    const title =
      typeof handle.getTitle === 'function'
        ? handle.getTitle(data)
        : handle.title

    if (title) {
      document.title = `${title} · PaperAirplane Accounts`
    }
  }, [matches])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  return (
    <AppShell
      header={{ height: 50 }}
      navbar={{
        width: desktopCollapsed ? 60 : 220,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Header
          burger={<Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />}
          onToggleSidebar={toggleDesktop}
          sidebarCollapsed={desktopCollapsed}
        />
      </AppShell.Header>

      <AppShell.Navbar>
        <Sidebar collapsed={desktopCollapsed} />
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  )
}
